import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Grid,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
  Chip,
  Tooltip,
} from "@mui/material";
import {
  useDeleteCompanyMutation,
  useGetCompanyQuery,
} from "../../../Api/attoDeskApi";
import { ICompanyDetails } from "../../../Api/Interface/api.interface";
import DeletePopup from "../../../Components/Delete/DeletePopup";
import useDebounce from "../../../Components/SearchDebonce/useDebounce";
import { useNavigate } from "react-router-dom";
import { useNotifier } from "../../../Core/Notifier";
import { CiSearch } from "react-icons/ci";
import AddIcon from "@mui/icons-material/Add";
import EditSharpIcon from "@mui/icons-material/EditSharp";
import DeleteOutlineSharpIcon from "@mui/icons-material/DeleteOutlineSharp";
import BusinessIcon from "@mui/icons-material/Business";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import PhoneOutlinedIcon from "@mui/icons-material/PhoneOutlined";
import LocationOnOutlinedIcon from "@mui/icons-material/LocationOnOutlined";
import LanguageOutlinedIcon from "@mui/icons-material/LanguageOutlined";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CompanyAvatar from "../../../Components/CompanyAvatar";

const LARAVEL_API_URL = process.env.REACT_APP_LARAVEL_API;

// ── Company Card ─────────────────────────────────────────────────────────────
const CompanyCard = ({
  company,
  index,
  onEdit,
  onDelete,
}: {
  company: ICompanyDetails;
  index: number;
  onEdit: () => void;
  onDelete: () => void;
}) => {
  const address = [company.companyCity, company.companyProvince]
    .filter(Boolean)
    .join(", ");

  return (
    <Box
      sx={{
        bgcolor: "#ffffff",
        border: "1px solid #e2e8f0",
        borderRadius: 3,
        overflow: "hidden",
        boxShadow: "0 1px 6px rgba(0,0,0,0.04)",
        transition: "all 0.2s",
        display: "flex",
        flexDirection: "column",
        width: "100%",
        height: "100%",
        "&:hover": {
          boxShadow: "0 8px 28px rgba(0,0,0,0.10)",
          borderColor: "#cbd5e1",
          transform: "translateY(-2px)",
        },
      }}
    >
      {/* Card top accent */}
      <Box
        sx={{
          height: 4,
          background: company.isDefault
            ? "linear-gradient(90deg, #2563eb, #2f3891)"
            : "linear-gradient(90deg, #e2e8f0, #f1f5f9)",
        }}
      />

      {/* Logo area */}
      <Box
        sx={{
          px: 3,
          pt: 2.5,
          pb: 2,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 2,
        }}
      >
        <CompanyAvatar
          name={company.companyName}
          logo={company.CompanyLogo}
          apiBase={LARAVEL_API_URL}
          size={56}
          rounded={2}
        />

        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
            <Typography
              sx={{
                fontSize: 15,
                fontWeight: 700,
                color: "#1e293b",
                lineHeight: 1.3,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {company.companyName}
            </Typography>
            {!!company.isDefault && (
              <Chip
                icon={<CheckCircleIcon sx={{ fontSize: "14px !important" }} />}
                label="Default"
                size="small"
                sx={{
                  height: 20,
                  fontSize: 10,
                  fontWeight: 700,
                  bgcolor: "#eff6ff",
                  color: "#2563eb",
                  border: "1px solid #bfdbfe",
                  "& .MuiChip-icon": { color: "#2563eb" },
                }}
              />
            )}
          </Box>
          {company.companySlog && (
            <Typography sx={{ fontSize: 11, color: "#94a3b8", mt: 0.25, fontStyle: "italic" }}>
              {company.companySlog}
            </Typography>
          )}
        </Box>

        {/* Index badge */}
        <Box sx={{ width: 26, height: 26, borderRadius: "50%", bgcolor: "#f1f5f9", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
          <Typography sx={{ fontSize: 11, fontWeight: 700, color: "#94a3b8" }}>{index + 1}</Typography>
        </Box>
      </Box>

      {/* Divider */}
      <Box sx={{ mx: 3, borderTop: "1px solid #f1f5f9" }} />

      {/* Info rows */}
      <Box sx={{ px: 3, py: 2, flex: 1, display: "flex", flexDirection: "column", gap: 1 }}>
        {company.companyEmail && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <EmailOutlinedIcon sx={{ fontSize: 14, color: "#94a3b8", flexShrink: 0 }} />
            <Typography sx={{ fontSize: 12, color: "#475569", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              {company.companyEmail}
            </Typography>
          </Box>
        )}
        {company.companyMobileNumber && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <PhoneOutlinedIcon sx={{ fontSize: 14, color: "#94a3b8", flexShrink: 0 }} />
            <Typography sx={{ fontSize: 12, color: "#475569" }}>
              {company.companyMobileNumber}
            </Typography>
          </Box>
        )}
        {address && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <LocationOnOutlinedIcon sx={{ fontSize: 14, color: "#94a3b8", flexShrink: 0 }} />
            <Typography sx={{ fontSize: 12, color: "#475569" }}>{address}</Typography>
          </Box>
        )}
        {company.companyWebSiteLink && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <LanguageOutlinedIcon sx={{ fontSize: 14, color: "#94a3b8", flexShrink: 0 }} />
            <Typography sx={{ fontSize: 12, color: "#2563eb", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              {company.companyWebSiteLink}
            </Typography>
          </Box>
        )}
        {company.companyHstNo && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Typography sx={{ fontSize: 11, color: "#94a3b8", fontWeight: 600 }}>HST:</Typography>
            <Typography sx={{ fontSize: 12, color: "#475569" }}>{company.companyHstNo}</Typography>
          </Box>
        )}
      </Box>

      {/* Actions */}
      <Box
        sx={{
          px: 3,
          py: 1.5,
          borderTop: "1px solid #f1f5f9",
          bgcolor: "#f8fafc",
          display: "flex",
          justifyContent: "flex-end",
          gap: 1,
        }}
      >
        <Tooltip title="Edit company">
          <IconButton
            size="small"
            onClick={onEdit}
            sx={{
              bgcolor: "#eff6ff",
              color: "#2563eb",
              borderRadius: 1.5,
              width: 32,
              height: 32,
              "&:hover": { bgcolor: "#dbeafe" },
            }}
          >
            <EditSharpIcon sx={{ fontSize: 15 }} />
          </IconButton>
        </Tooltip>
        <Tooltip title="Delete company">
          <IconButton
            size="small"
            onClick={onDelete}
            sx={{
              bgcolor: "#fef2f2",
              color: "#dc2626",
              borderRadius: 1.5,
              width: 32,
              height: 32,
              "&:hover": { bgcolor: "#fee2e2" },
            }}
          >
            <DeleteOutlineSharpIcon sx={{ fontSize: 15 }} />
          </IconButton>
        </Tooltip>
      </Box>
    </Box>
  );
};

// ── Main Component ────────────────────────────────────────────────────────────
const Index = () => {
  const navigate = useNavigate();
  const { showErrorMessage, showMessage } = useNotifier();
  const [searchQuery, setSearchQuery] = useState("");
  const [currentSearchQuery, setCurrentSearchQuery] = useState("");
  const debouncedSearchQuery = useDebounce(searchQuery, 300);
  const [coursingToDelete, setCoursingToDelete] = useState<ICompanyDetails | null>(null);
  const [openDeleteCategory, setOpenDeleteCategory] = useState(false);

  const { data, isLoading, isError, refetch } = useGetCompanyQuery({
    searchText: currentSearchQuery,
    id: "",
  });

  useEffect(() => {
    if (debouncedSearchQuery.length >= 4 || debouncedSearchQuery === "") {
      setCurrentSearchQuery(debouncedSearchQuery);
      refetch();
    }
  }, [debouncedSearchQuery]);

  const [deleteCategory] = useDeleteCompanyMutation();
  const handleDelete = async (id: string) => {
    try {
      const response = await deleteCategory(id).unwrap();
      if (response.status) {
        showMessage("Company deleted successfully");
        setOpenDeleteCategory(false);
      } else {
        showErrorMessage("Failed to delete the company");
      }
    } catch {
      showErrorMessage("Failed to delete the company");
    }
  };

  const coursings: ICompanyDetails[] = Array.isArray(data?.data)
    ? (data?.data as ICompanyDetails[])
    : [];

  const defaultCount = coursings.filter((c) => c.isDefault).length;

  if (isLoading)
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="40vh" flexDirection="column" gap={2}>
        <CircularProgress size={36} sx={{ color: "#2563eb" }} />
        <Typography sx={{ fontSize: 13, color: "#64748b" }}>Loading companies...</Typography>
      </Box>
    );

  return (
    <Box sx={{ bgcolor: "#f1f5f9", minHeight: "100vh" }}>

      {/* ── Hero Header ── */}
      <Box sx={{
        background: "linear-gradient(135deg, #1e293b 0%, #0f172a 100%)",
        px: { xs: 2, md: 4 }, py: 3,
        display: "flex", alignItems: "center", justifyContent: "space-between",
        flexWrap: "wrap", gap: 2,
        boxShadow: "0 4px 24px rgba(0,0,0,0.15)",
      }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <Box sx={{
            width: 48, height: 48, borderRadius: 2.5,
            background: "linear-gradient(135deg, #2563eb, #2f3891)",
            display: "flex", alignItems: "center", justifyContent: "center",
            boxShadow: "0 4px 12px rgba(37,99,235,0.4)",
          }}>
            <BusinessIcon sx={{ color: "#fff", fontSize: 24 }} />
          </Box>
          <Box>
            <Typography sx={{ fontSize: 20, fontWeight: 800, color: "#ffffff", lineHeight: 1.2 }}>
              Companies
            </Typography>
            <Typography sx={{ fontSize: 12, color: "#94a3b8", mt: 0.25 }}>
              {coursings.length} {coursings.length === 1 ? "company" : "companies"} registered
            </Typography>
          </Box>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => navigate("/Company/create")}
          sx={{
            background: "linear-gradient(135deg, #2563eb, #1d4ed8)",
            borderRadius: 2, fontWeight: 700,
            boxShadow: "0 4px 12px rgba(37,99,235,0.4)",
            "&:hover": { background: "linear-gradient(135deg, #1d4ed8, #1e40af)" },
          }}
        >
          Add Company
        </Button>
      </Box>

      <Box sx={{ px: { xs: 2, md: 4 }, py: 3 }}>

        {/* ── Stats Row ── */}
        <Grid container spacing={2} sx={{ mb: 3 }}>
          {[
            { label: "Total Companies", value: coursings.length, color: "#2563eb", bg: "#eff6ff", border: "#bfdbfe" },
            { label: "Default Company", value: defaultCount, color: "#16a34a", bg: "#f0fdf4", border: "#bbf7d0" },
            { label: "With Logo", value: coursings.filter(c => c.CompanyLogo).length, color: "#2f3891", bg: "#f5f3ff", border: "#ddd6fe" },
          ].map((stat) => (
            <Grid item xs={12} sm={4} key={stat.label}>
              <Box sx={{ bgcolor: stat.bg, border: `1px solid ${stat.border}`, borderRadius: 2.5, px: 2.5, py: 2, display: "flex", alignItems: "center", gap: 2 }}>
                <Typography sx={{ fontSize: 28, fontWeight: 800, color: stat.color, lineHeight: 1 }}>{stat.value}</Typography>
                <Typography sx={{ fontSize: 13, fontWeight: 600, color: stat.color, opacity: 0.8 }}>{stat.label}</Typography>
              </Box>
            </Grid>
          ))}
        </Grid>

        {/* ── Search Bar ── */}
        <Box sx={{ mb: 3 }}>
          <TextField
            size="small"
            placeholder="Search companies..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            sx={{
              maxWidth: 360,
              "& .MuiOutlinedInput-root": {
                borderRadius: 2,
                bgcolor: "#ffffff",
                boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
                "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "#2563eb" },
              },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <CiSearch style={{ fontSize: 18, color: "#94a3b8" }} />
                </InputAdornment>
              ),
            }}
          />
        </Box>

        {/* ── Company Cards ── */}
        {isError || coursings.length === 0 ? (
          <Box sx={{
            bgcolor: "#ffffff", border: "1px solid #e2e8f0", borderRadius: 3,
            py: 10, textAlign: "center",
            boxShadow: "0 1px 6px rgba(0,0,0,0.04)",
          }}>
            <Box sx={{ width: 64, height: 64, borderRadius: "50%", bgcolor: "#f1f5f9", display: "flex", alignItems: "center", justifyContent: "center", mx: "auto", mb: 2 }}>
              <BusinessIcon sx={{ fontSize: 30, color: "#94a3b8" }} />
            </Box>
            <Typography sx={{ fontSize: 16, fontWeight: 700, color: "#1e293b", mb: 0.5 }}>
              {isError ? "Error loading companies" : "No companies found"}
            </Typography>
            <Typography sx={{ fontSize: 13, color: "#94a3b8", mb: 3 }}>
              {isError ? "Please try refreshing the page" : searchQuery ? "Try a different search term" : "Add your first company to get started"}
            </Typography>
            {!isError && !searchQuery && (
              <Button variant="contained" startIcon={<AddIcon />} onClick={() => navigate("/Company/create")}
                sx={{ background: "linear-gradient(135deg, #2563eb, #1d4ed8)", borderRadius: 2, fontWeight: 700 }}>
                Add Company
              </Button>
            )}
          </Box>
        ) : (
          <Grid container spacing={2.5} alignItems="stretch">
            {coursings.map((company, index) => (
              <Grid item xs={12} sm={6} lg={4} xl={3} key={company.id} sx={{ display: "flex" }}>
                <CompanyCard
                  company={company}
                  index={index}
                  onEdit={() => navigate(`/company-history/${company.id}`)}
                  onDelete={() => { setCoursingToDelete(company); setOpenDeleteCategory(true); }}
                />
              </Grid>
            ))}
          </Grid>
        )}

        {/* Record count */}
        {coursings.length > 0 && (
          <Box sx={{ mt: 3, textAlign: "center" }}>
            <Typography sx={{ fontSize: 12, color: "#94a3b8" }}>
              Showing {coursings.length} {coursings.length === 1 ? "company" : "companies"}
            </Typography>
          </Box>
        )}
      </Box>

      {/* ── Delete Confirmation ── */}
      {coursingToDelete && (
        <DeletePopup
          open={openDeleteCategory}
          handleCloseDelete={() => { setCoursingToDelete(null); setOpenDeleteCategory(false); }}
          onConfirm={async () => { await handleDelete(coursingToDelete.id.toString()); }}
          title="Delete Company"
          content={`Are you sure you want to delete "${coursingToDelete.companyName}"? This action cannot be undone.`}
        />
      )}
    </Box>
  );
};

export default Index;
